/**
 * `useUpdateProjectMembers` mutation hook.
 *
 * Wraps the raw `updateProjectMembers` service fn (now also colocated in
 * this slice's `api/project-members.api.ts`). Cache invalidation
 * targets the entity-level project-members key so the modal re-fetches the
 * member list after add/remove.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { PROJECTS_QUERY_KEYS, updateProject, updateProjectMembers } from '@/entities/project';
import { PROJECT_MEMBER_QUERY_KEYS } from '@/entities/project-member';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { track } from '@/shared/analytics';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { readProjectRoleRefusal } from '../lib/project-role-refusal';

export { updateProjectMembers };

interface UpdateProjectMembersPayload {
  userIds: string[];
  roles?: Array<{ userId: string; roleId: string }>;
}

interface UseUpdateProjectMembersOptions {
  workspaceId: string;
  /** Platform-api project id — used for the member-update endpoint, analytics, and cache invalidation. */
  platformProjectId: string;
}

export function useUpdateProjectMembers(options: UseUpdateProjectMembersOptions) {
  const { t } = useTranslation('projects');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateProjectMembersPayload) =>
      updateProjectMembers(options.platformProjectId, payload),
    onSuccess: () => {
      track('project_members_updated', { project_id: options.platformProjectId });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_MEMBER_QUERY_KEYS.byProject(
          options.workspaceId,
          options.platformProjectId,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_MEMBER_QUERY_KEYS.roleAssignments(options.platformProjectId),
      });
      // Bump workspace-api project.updated_at so the Projects list "Last Activity"
      // column reflects this member change. Best-effort: a failure here just
      // means the activity timestamp stays slightly stale; never surface to UI.
      updateProject(options.platformProjectId, {})
        .then(() => {
          // Bust the per-workspace projects-list cache so navigating back picks
          // up the new lastActivityAt. Slice prefix-matches every paginated /
          // filtered list query under this workspace.
          void queryClient.invalidateQueries({
            queryKey: PROJECTS_QUERY_KEYS.list(options.workspaceId).slice(0, 3),
          });
        })
        .catch((error: unknown) => {
          logger.error('Failed to bump project last activity after member update:', error);
        });
    },
    onError: (error) => {
      logger.error('Failed to update project members:', error);
      notificationService.showError(updateFailureMessage(error, t), {
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    },
  });
}

/**
 * This request keeps its 403s out of the global error toast, so each one is explained here:
 * a refused role by name, any other refusal in the server's own words.
 */
function updateFailureMessage(error: unknown, t: TFunction<'projects'>): string {
  const refusedRoleName = readProjectRoleRefusal(error);
  if (refusedRoleName !== undefined) {
    return t('manageProjectMembers.useUpdateProjectMembers.cannotAssignRole', {
      role: refusedRoleName,
    });
  }
  const fallback = t('manageProjectMembers.useUpdateProjectMembers.failedToUpdateProjectMembers');
  const problem = parseProblemDetail(error, fallback);
  return problem.status === 403 ? problem.detail : fallback;
}
